import { useMemo, useState } from 'react';
import {
  Badge,
  Banner,
  Box,
  Button,
  DataTable,
  DataTableBody,
  Group,
  Heading,
  Spinner,
  Text,
} from '@optiaxiom/react';
import { IconLock } from '@optiaxiom/icons';
import {
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
  type SortingState,
} from '@tanstack/react-table';
import { useAppInstallations, type AppInstallationItem } from '@/lib/api';
import { formatDate } from './formatDate';
import appsStyles from './AppsPage.module.css';
import styles from './InstallationsTab.module.css';

/**
 * Orders rows: accessible installs first (inaccessible sink to the bottom), then within each
 * section by last updated, newest first. Returns a new array.
 */
function sortInstallations(items: AppInstallationItem[]): AppInstallationItem[] {
  const time = (iso: string) => {
    const t = new Date(iso).getTime();
    return Number.isNaN(t) ? 0 : t;
  };
  return [...items].sort(
    (a, b) =>
      Number(b.accessible) - Number(a.accessible) ||
      time(b.lastUpdated) - time(a.lastUpdated),
  );
}

/** The subtitle under a row's tracker id: instance and org, or the no-access note. */
function rowSubtitle(row: AppInstallationItem): string {
  if (row.accessible) {
    return [row.instanceName, row.organization].filter(Boolean).join(' · ');
  }
  return "OCP instance · you don't have access";
}

function TrackerCell({ row }: { row: AppInstallationItem }) {
  return (
    <Group flexDirection="column" gap="4" alignItems="start">
      {row.accessible ? (
        <Text fontFamily="mono" fontWeight="600" color="fg.success">
          {row.trackerId}
        </Text>
      ) : (
        <Group flexDirection="row" gap="6" alignItems="center" color="fg.tertiary">
          <Box asChild>
            <span>
              <IconLock size={14} />
            </span>
          </Box>
          <Text fontFamily="mono" fontWeight="600" color="fg.secondary">
            {row.trackerId}
          </Text>
        </Group>
      )}
      <Text
        fontSize="sm"
        color="fg.secondary"
        className={row.accessible ? undefined : styles.noAccessNote}
      >
        {rowSubtitle(row)}
      </Text>
    </Group>
  );
}

export function InstallationsTab({ appId }: { appId: string }) {
  const { data, isLoading, isError, isFetching, refetch } = useAppInstallations(appId);
  const [sorting, setSorting] = useState<SortingState>([]);

  // Default order (no active column sort): accessible installs first, then newest. Clicking a
  // column header re-sorts by that column via DataTable.
  const items = useMemo(() => sortInstallations(data?.items ?? []), [data?.items]);

  const columns = useMemo<ColumnDef<AppInstallationItem>[]>(
    () => [
      {
        accessorKey: 'trackerId',
        header: 'Tracker ID / Instance',
        size: 380,
        cell: ({ row }) => <TrackerCell row={row.original} />,
      },
      {
        accessorKey: 'version',
        header: 'Version',
        size: 130,
        cell: ({ getValue }) => <Text fontFamily="mono">{getValue<string>()}</Text>,
      },
      {
        accessorKey: 'region',
        header: 'Region',
        size: 120,
        cell: ({ getValue }) => (
          <Badge intent="neutral" variant="subtle" textTransform="uppercase">
            {getValue<string>()}
          </Badge>
        ),
      },
      {
        accessorKey: 'installedAt',
        header: 'Installation date',
        size: 170,
        cell: ({ getValue }) => <Text color="fg.secondary">{formatDate(getValue<string>())}</Text>,
      },
      {
        accessorKey: 'lastUpdated',
        header: 'Last updated',
        size: 170,
        cell: ({ getValue }) => <Text color="fg.secondary">{formatDate(getValue<string>())}</Text>,
      },
    ],
    [],
  );

  const table = useReactTable({
    data: items,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  if (isLoading) {
    return (
      <Group flexDirection="column" alignItems="center" className={appsStyles.stateContainer}>
        <Spinner />
        <Text color="fg.secondary">Loading installations…</Text>
      </Group>
    );
  }

  if (isError) {
    return (
      <Group flexDirection="column" alignItems="center" className={appsStyles.stateContainer}>
        <Heading level="2" fontWeight="700">
          Couldn&rsquo;t load installations
        </Heading>
        <Text color="fg.secondary">Something went wrong loading this app&rsquo;s installations.</Text>
        <Button appearance="primary" onClick={() => void refetch()} loading={isFetching} disabled={isFetching}>
          Try again
        </Button>
      </Group>
    );
  }

  const degradedShards = data?.degradedShards ?? [];

  if (items.length === 0) {
    return (
      <Group flexDirection="column" alignItems="center" className={appsStyles.stateContainer}>
        <Heading level="2" fontWeight="700">
          No installations yet
        </Heading>
        <Text color="fg.secondary">
          Installations appear here once someone installs this app from the OCP CLI or App Directory.
        </Text>
      </Group>
    );
  }

  return (
    <Group flexDirection="column" gap="12" alignItems="stretch">
      {degradedShards.length > 0 && (
        <Banner intent="warning" rounded="lg">
          <Text>
            Some regions were unavailable ({degradedShards.join(', ')}); this list may be incomplete.
          </Text>
        </Banner>
      )}

      <DataTable table={table} className={`${appsStyles.dataTable}`}>
        <DataTableBody />
      </DataTable>
    </Group>
  );
}
